import { useClinicDay } from '@hooks/useClinicDay'
import { useAppSelector } from '@store/hooks'
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useLayoutEffect,
  useCallback,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './MessageCenterCallsStyle.css'
import { usePageContentGridScrollMaxHeight } from '@hooks/usePageContentGridScrollMaxHeight'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import useCalls from './useCalls'
import MessageCenterCallAudio from './MessageCenterCallAudio.component'
import { sentimentsTemplate, typeTemplate } from './templates/Logs.templates'
import { ItemModel } from '@syncfusion/ej2-react-splitbuttons'
import { ICalls } from '@dataTypes/calls.type'
import { formatSecondsToString } from '@utils/time.util'
import CallerDetails from './CallerDetails.component'
import {
  ColumnModel,
  RecordClickEventArgs,
  ActionEventArgs,
} from '@syncfusion/ej2-react-grids'
import {
  Button,
  SearchWithFilter,
  AiReceptionistIcon,
} from '@components/common'
import { EmptyState } from '@components/ui'
import Dropdown from '@components/common/Dropdown/Dropdown.component'
import { useMessageCenterTabLayer } from '../MessageCenterTabLayer.context'
import { AppointmentFilter, TranscriptData } from '@dataTypes/message.types'
import { IContact } from '@dataTypes/contact.type'
import { MenuComponent } from '@syncfusion/ej2-react-navigations'
import FloatingMenu from './templates/FloatingMenu.component'
import {
  AddContactModal,
  AISummaryModal,
  AudioTranscription,
} from '@components/layout/Modals'
import MessageCenterDeleteCallLogModal from '../MessageCenterDeleteCallLogModal.component'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { TextAlign } from '@constants/Common/GridComponent.contants'
import {
  formatCallDate,
  getCallTimestamp,
  getCallAiPlaceholder,
  getCallDirection,
  isCallAiInProgress,
} from '@utils/call.util'
import { getFullName } from '@utils/fullname.util'
import {
  isAiAssistedCall,
  AI_ASSISTED_CALL_HINT,
} from '@utils/aiReceptionist.util'
import { useAiReceptionistActorId } from '@hooks/useAiReceptionistActorId'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import type { Patient } from '@dataTypes/PatientInfo.types'

const parseCallTranscript = (value?: string | null): TranscriptData[] => {
  if (!value?.trim()) return []
  try {
    const parsed = JSON.parse(value) as unknown
    return Array.isArray(parsed) ? (parsed as TranscriptData[]) : []
  } catch {
    return []
  }
}
import { fetchPatientById } from '@services/api/patient.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import { showCommunicationLogNotFoundToast } from '@utils/patientProfileCommunicationNavigation.util'

type TagsCellProps = {
  tags: string[]
  emptyLabel?: string
}

const TagsCellInner = ({
  tags,
  emptyLabel = 'N/A',
}: TagsCellProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const measureContainerRef = useRef<HTMLDivElement | null>(null)
  const measurePlusRef = useRef<HTMLSpanElement | null>(null)
  const measureTagRefs = useRef<Array<HTMLSpanElement | null>>([])
  const [visibleCount, setVisibleCount] = useState<number>(tags.length)

  const recomputeVisibleCount = useCallback(() => {
    const container = containerRef.current
    const measureContainer = measureContainerRef.current
    if (!container || !measureContainer) return

    const availableWidth = container.clientWidth
    if (!availableWidth) return

    const styles = window.getComputedStyle(measureContainer)
    const gapValue = (styles as unknown as { gap?: string }).gap
    const gap =
      parseFloat(styles.columnGap || '') || parseFloat(gapValue || '') || 4

    const tagWidths = tags.map((_, idx) => {
      const el = measureTagRefs.current[idx]
      return el ? el.getBoundingClientRect().width : 0
    })

    const measurePlusWidth = (hiddenCount: number): number => {
      if (!measurePlusRef.current) return 0
      measurePlusRef.current.textContent = `+${hiddenCount}`
      return measurePlusRef.current.getBoundingClientRect().width
    }

    // Find max number of tags that fit on one line, leaving room for the +N chip if needed
    for (let vc = tags.length; vc >= 0; vc--) {
      const hidden = tags.length - vc
      const plusWidth = hidden > 0 ? measurePlusWidth(hidden) : 0

      let total = 0
      for (let i = 0; i < vc; i++) total += tagWidths[i] ?? 0
      if (vc > 1) total += gap * (vc - 1)
      if (hidden > 0) {
        if (vc > 0) total += gap
        total += plusWidth
      }

      if (total <= availableWidth + 0.5) {
        setVisibleCount(vc)
        return
      }
    }

    setVisibleCount(0)
  }, [tags])

  useLayoutEffect(() => {
    setVisibleCount(tags.length)
    recomputeVisibleCount()
  }, [tags, recomputeVisibleCount])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => recomputeVisibleCount())
      ro.observe(el)
      return () => ro.disconnect()
    }

    window.addEventListener('resize', recomputeVisibleCount)
    return () => window.removeEventListener('resize', recomputeVisibleCount)
  }, [recomputeVisibleCount])

  if (!tags.length) {
    return (
      <div className="call-tags-cell" ref={containerRef}>
        <div className="call-tags-row">
          <span
            className={
              emptyLabel === 'Analyzing...' ? 'call-ai-placeholder' : 'call-tag'
            }
          >
            {emptyLabel}
          </span>
        </div>
      </div>
    )
  }

  const hiddenCount = Math.max(tags.length - visibleCount, 0)
  const hiddenTags = hiddenCount ? tags.slice(visibleCount) : []

  return (
    <div className="call-tags-cell" ref={containerRef}>
      <div className="call-tags-row">
        {tags.slice(0, visibleCount).map((tag, idx) => (
          <span
            key={`${tag}-${idx}`}
            className={`call-tag ${tag?.toLowerCase().replaceAll(' ', '-')}`}
          >
            {tag}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="call-tag more" title={hiddenTags.join(', ')}>
            {`+${hiddenCount}`}
          </span>
        )}
      </div>

      {/* Hidden measuring row */}
      <div className="call-tags-measure" ref={measureContainerRef}>
        {tags.map((tag, idx) => (
          <span
            key={`${tag}-measure-${idx}`}
            ref={(el) => {
              measureTagRefs.current[idx] = el
            }}
            className={`call-tag ${tag?.toLowerCase().replaceAll(' ', '-')}`}
          >
            {tag}
          </span>
        ))}
        <span ref={measurePlusRef} className="call-tag more">
          +0
        </span>
      </div>
    </div>
  )
}

const TagsCell = React.memo(TagsCellInner)
TagsCell.displayName = 'TagsCell'

const callsDropdownOptions: AppointmentFilter[] = ['week', 'month', 'year']

function MessageCenterCalls(): React.ReactElement {
  const { setTabLayerActions } = useMessageCenterTabLayer()
  const aiActorId = useAiReceptionistActorId()
  const { openNoteEditor, updateNoteEditor, closeNoteEditor } =
    useNoteEditorOverlay()
  const clinicTimeZone = useAppSelector(
    (state) => state.clinic.selectedClinic?.time_zone || 'UTC',
  )
  const clinicDay = useClinicDay(clinicTimeZone)
  const callHooks = useCalls({
    onOpenNotesOverlay: (state, patient): void => {
      openNoteEditor({
        patient: patient ?? null,
        contextId: state.contextId ?? null,
        contextType: state.contextType ?? null,
        contextName: state.contextName ?? null,
        contactId: state.contactId ?? null,
        openFirstNote: state.openFirstNote ?? false,
        openNoteId: state.openNoteId ?? null,
        pagePreviousState: state.previousPage ?? null,
        initialView: state.openFirstNote ? 'list' : 'add',
        overlayBackLabel: 'Back to calls',
        editorBackButtonLabel: 'Back to calls',
        openContextNotesByDefault: true,
        hideNotesTable: true,
        notesFetchLoadingContainer: 'body',
        portalTarget: '.page-content',
      })
      if (patient?.patient_id) return
      if (state.patientId) {
        void fetchPatientById(state.patientId)
          .then((response) => {
            updateNoteEditor({
              patient: response.data.patient as unknown as Patient,
            })
          })
          .catch(() => {
            closeNoteEditor()
          })
      }
    },
  })

  const hasNoCallLogs = callHooks.callLogs.length === 0
  const showEmptyState =
    !callHooks.callsLoading && callHooks.filteredCallLogs.length === 0

  useEffect(() => {
    setTabLayerActions(
      <div className="mc-calls-buttons">
        <SearchWithFilter
          searchIcon="mgc_search_2_line"
          filterIcon="mgc_settings_2_line"
          placeholder="Search"
          searchQuery={callHooks.searchQuery}
          onChange={callHooks.handleSearch}
          className="mc-call-filter"
          selectedFilters={callHooks.filters}
          filters={{
            status: [
              'Feedback',
              'Inquiry',
              'Resolved',
              'Unresolved',
              'Follow-up required',
              'High priority',
            ],
            sentiment: ['Positive', 'Negative', 'Neutral'],
            type: ['Incoming', 'Outgoing', 'Missed', 'Decline'],
            aiReceptionist: ['Assisted by AI'],
          }}
          onFilterChange={callHooks.handleFilterChange}
          filterLabels={{
            status: 'Status',
            sentiment: 'Sentiment',
            type: 'Type',
            aiReceptionist: 'AI receptionist',
          }}
        />
        <Dropdown
          options={callsDropdownOptions}
          selectedOption={callsDropdownOptions[callHooks.durationIndex]}
          onOptionChange={callHooks.handleDropdownChange}
          renderOption={(option) => <span>{`This ${option}`}</span>}
          renderSelectedOption={(option) => <span>{`This ${option}`}</span>}
        />
      </div>,
    )
    return () => setTabLayerActions(null)
  }, [
    callHooks.durationIndex,
    callHooks.filters,
    callHooks.searchQuery,
    setTabLayerActions,
  ])

  const menuRef = useRef<MenuComponent>(null)
  const menuContainerRef = useRef<HTMLDivElement>(null)
  const callsGridSectionRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { showToast } = useNotificationToast()
  const appliedDeepLinkCallIdRef = useRef<string | null>(null)

  const scrollExpandedCallIntoView = useCallback((): void => {
    requestAnimationFrame(() => {
      callsGridSectionRef.current
        ?.querySelector('.e-row-expanded')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }, [])

  const finalizeCallDeepLink = useCallback((): void => {
    const routeState = location.state as {
      callId?: string
      previousPage?: unknown
    } | null
    const callId = routeState?.callId
    if (!callId) return

    appliedDeepLinkCallIdRef.current = callId
    navigate(location.pathname, {
      replace: true,
      state: routeState?.previousPage
        ? { previousPage: routeState.previousPage }
        : null,
    })
    scrollExpandedCallIntoView()
  }, [location.pathname, location.state, navigate, scrollExpandedCallIntoView])

  const callsGridDataBoundFrameRef = useRef<number | null>(null)

  const handleCallsGridDataBound = useCallback((): void => {
    if (callsGridDataBoundFrameRef.current != null) {
      cancelAnimationFrame(callsGridDataBoundFrameRef.current)
    }
    callsGridDataBoundFrameRef.current = requestAnimationFrame(() => {
      callsGridDataBoundFrameRef.current = null
      if (callHooks.retryPendingExpand()) {
        finalizeCallDeepLink()
        return
      }
      callHooks.reexpandExpandedCall()
    })
  }, [callHooks, finalizeCallDeepLink])

  useEffect(() => {
    return () => {
      if (callsGridDataBoundFrameRef.current != null) {
        cancelAnimationFrame(callsGridDataBoundFrameRef.current)
      }
    }
  }, [])

  const handleCallsGridActionComplete = useCallback(
    (args: ActionEventArgs): void => {
      if (args.requestType !== 'paging') return
      if (callHooks.retryPendingExpand()) {
        finalizeCallDeepLink()
      }
    },
    [callHooks, finalizeCallDeepLink],
  )

  const callsGridScrollStyle = usePageContentGridScrollMaxHeight(
    callsGridSectionRef,
    [callHooks.filteredCallLogs.length],
    { disabled: showEmptyState },
  )

  // Add click outside handler to close menu
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node

      // Check if clicked inside our menu wrapper
      const isInsideMenuRef = menuContainerRef.current?.contains(target)

      // Check if clicked inside Syncfusion's submenu
      const element = target as Element
      const isInsideSyncfusionPopup =
        element.closest?.('.e-menu-wrapper') !== null

      if (!isInsideMenuRef && !isInsideSyncfusionPopup) {
        callHooks.setMenuAnchor(null)
        menuRef.current?.close()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [callHooks])

  useEffect(() => {
    const routeState = location.state as {
      callId?: string
      previousPage?: unknown
    } | null
    const callId = routeState?.callId
    if (!callId) {
      appliedDeepLinkCallIdRef.current = null
      return
    }
    if (appliedDeepLinkCallIdRef.current === callId) return
    if (callHooks.callsLoading) return

    const previousPage = routeState?.previousPage
    const clearDeepLinkState = (): void => {
      navigate(location.pathname, {
        replace: true,
        state: previousPage ? { previousPage } : null,
      })
    }

    const idx = callHooks.filteredCallLogs.findIndex(
      (call) => call.call_id === callId,
    )

    if (idx === -1) {
      appliedDeepLinkCallIdRef.current = callId
      showCommunicationLogNotFoundToast(showToast, 'call')
      clearDeepLinkState()
      return
    }

    const expanded = callHooks.expandCallById(callId)
    if (expanded) {
      finalizeCallDeepLink()
    }
  }, [
    callHooks,
    finalizeCallDeepLink,
    location.pathname,
    location.state,
    navigate,
    showToast,
  ])

  const getPercentageIcon = (className: string): JSX.Element => {
    if (className === 'decrease')
      return <i className="percentage-icon mgc_arrow_down_line" />
    else if (className === 'increase')
      return <i className="percentage-icon mgc_arrow_up_line" />
    else return <></>
  }

  const getPercentageBadgeClassname = (
    item: string,
    result: string,
  ): string => {
    const invertedMetrics = ['handlingTime', 'waitTime']
    if (invertedMetrics.includes(item)) {
      if (result === 'decrease') return 'improved'
      else if (result === 'increase') return 'worsen'
      else return 'neutral'
    }

    if (result === 'decrease') return 'worsen'
    else if (result === 'increase') return 'improved'
    else return 'neutral'
  }

  const statusTemplate = (props: ICalls): JSX.Element => {
    const tags = props.tags ?? []
    return <TagsCell tags={tags} emptyLabel={getCallAiPlaceholder(props)} />
  }

  const notesTemplate = (props: ICalls): JSX.Element => {
    const items: ItemModel[] = [{ text: 'Call Notes' }]
    if (props.notes) {
      props.notes.split('\n').forEach((note) => {
        items.push({
          text: note,
          iconCss: 'ti ti-point-filled',
        })
      })
    }
    return (
      <div className="action-buttons">
        {props.call_notes && props.call_notes.length ? (
          <Button
            className="act-button act-button-edit"
            onClick={() => callHooks.handleViewNotes(props)}
            title="View notes"
          >
            <span className="mgc_document_fill" />
          </Button>
        ) : (
          <p className="mb-0">-</p>
        )}
      </div>
    )
  }

  const ActionStatus = (props: ICalls): JSX.Element => {
    const baseActionItems = [...callHooks.actionItems]

    // Check if items already exist in the base array
    const hasDownloadRecording = baseActionItems.some(
      (item) => item.text === 'Download recording',
    )
    const hasAddContact = baseActionItems.some(
      (item) => item.text === 'Add contact',
    )
    const hasSetAppointment = baseActionItems.some(
      (item) => item.text === 'Set appointment',
    )

    // Add download recording if recording URL exists and item is not already present
    if (props.recording_url && !hasDownloadRecording) {
      baseActionItems.unshift(callHooks.downloadRecordingItem)
    }

    // Add set appointment if contact has patient_id and item is not already present
    if (props.contact?.patient_id && !hasSetAppointment) {
      baseActionItems.unshift(callHooks.setAppointmentItem)
    }

    // Add contact if no contact exists and item is not already present
    if (!props.contact && !hasAddContact) {
      baseActionItems.unshift(callHooks.addContactItem)
    }

    const _contact: IContact = props.contact ?? {
      clinic_id: '',
      title: 'Mr.',
      first_name: '',
      last_name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      cellphone:
        getCallDirection(props) === 'Incoming'
          ? props.caller_number
          : props.receiver_number,
      email: '',
    }

    const buttonRef = useRef<HTMLButtonElement>(null)

    return (
      <div className="d-flex position-relative justify-content-center">
        <Button
          className="action-button-filled"
          onClick={() => {
            callHooks.setSelectedContact(_contact)
            callHooks.makeCall(
              ['Outgoing', 'Decline'].includes(props.call_type)
                ? props.receiver_number
                : props.caller_number,
            )
          }}
        >
          <i className="mgc_phone_fill abf-icon" />
        </Button>
        <Button className="action-button" disabled>
          <i className="ti ti-mail-filled" />
        </Button>
        <Button
          className="action-button"
          onClick={() => {
            callHooks.handleSendMessage(_contact)
          }}
        >
          <i className="ti ti-message-filled" />
        </Button>
        <div className="more-options-container" ref={menuContainerRef}>
          <button
            type="button"
            className={`unstyled-button calls-kebab-btn${
              callHooks.menuAnchor?.id === props.call_id ? ' active' : ''
            }`}
            aria-label="More options"
            ref={buttonRef}
            onClick={() => {
              callHooks.handleOnClickDropDown(props, buttonRef, baseActionItems)
            }}
          >
            <i className="mgc_more_2_line" aria-hidden />
          </button>
        </div>
      </div>
    )
  }

  const onRowClick = (args: RecordClickEventArgs): void => {
    const target = args.target as HTMLElement | undefined
    const rowData = args.rowData as ICalls | undefined

    if (!target || !rowData || args.rowIndex === undefined) return
    if (!(rowData.summary || rowData.recording_url)) return
    if (target.closest('.call-detail-row, .expand-btn')) return
    if (target.closest('button, a, input, textarea, select')) return
    if (target.closest('.calls-kebab-btn, .note-icon')) return

    callHooks.toggleDetail(args.rowIndex, rowData.call_id)
  }

  const nameTemplate = (props: ICalls): JSX.Element => {
    let fullName: string = 'Unknown caller'
    let classname: string = 'unknown-full-name'
    if (props.contact) {
      fullName = getFullName(props.contact)
      classname = 'full-name'
    }
    return (
      <div className="d-flex align-items-center gap-1">
        <span className={`mcc-dg-name ${classname}`}>{fullName}</span>
        {isAiAssistedCall(props, aiActorId) ? (
          <AiReceptionistIcon label={AI_ASSISTED_CALL_HINT} />
        ) : null}
        <button
          type="button"
          className={`unstyled-button expand-btn mgc_right_small_fill${!(props.summary || props.recording_url) ? ' disabled' : ''}`}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            callHooks.toggleCallById(props.call_id)
          }}
          aria-label="Expand row"
          disabled={!(props.summary || props.recording_url)}
        />
      </div>
    )
  }

  const callDetailTemplate = (props: ICalls): JSX.Element => {
    const stop = (e: React.SyntheticEvent): void => e.stopPropagation()
    const stopMouseDown = (e: React.MouseEvent): void => {
      e.stopPropagation()
      if ((e.target as HTMLElement).tagName !== 'INPUT') {
        e.preventDefault()
      }
    }
    return (
      <div
        className="call-detail-row"
        onMouseDown={stopMouseDown}
        onMouseUp={stop}
        onClick={stop}
        onDoubleClick={stop}
        onTouchStart={stop}
        onTouchEnd={stop}
        onKeyDown={stop}
      >
        <MessageCenterCallAudio
          call={props}
          onNeedCanonicalize={callHooks.generatePresignedUrlForCall}
        />
        <div className="call-detail-summary">
          <span className="call-detail-summary-label">AI summary</span>
          <span className="call-detail-summary-text">
            {props.summary
              ?.split('\n')
              .map((line) => line.trim().replace(/^-+\s*/, ''))
              .filter(Boolean)
              .join(' ') ||
              (isCallAiInProgress(props)
                ? 'Analyzing...'
                : 'No summary available.')}
          </span>
        </div>
      </div>
    )
  }

  const columns: ColumnModel[] = useMemo(() => {
    const baseColumns = [
      {
        headerText: 'Call type',
        field: 'call_display_type',
        template: typeTemplate,
        textAlign: TextAlign.Center,
        width: '6%',
      },
      {
        headerText: 'Contact name',
        field: 'contact.first_name',
        template: nameTemplate,
        textAlign: TextAlign.Left,
        width: '12%',
      },
      {
        headerText: 'Contact number',
        field: '_contact',
        textAlign: TextAlign.Left,
        width: '12%',
      },
      {
        headerText: 'Call date',
        field: '_call_timestamp',
        type: 'number',
        template: (call: ICalls & { _call_date: string }) => (
          <span>{call._call_date}</span>
        ),
        textAlign: TextAlign.Left,
        width: '15%',
      },
      {
        headerText: 'Tags',
        field: 'tags',
        template: statusTemplate,
        textAlign: TextAlign.Left,
        width: '11%',
      },
      {
        headerText: 'Sentiment',
        field: 'sentiments',
        template: sentimentsTemplate,
        textAlign: TextAlign.Left,
        width: '11%',
      },
      {
        headerText: 'Notes',
        field: 'call_notes',
        template: notesTemplate,
        textAlign: 'Center' as TextAlign,
        width: '9%',
      },
    ]

    // Only include action column when caller details are not shown
    if (!callHooks.showCallerDetails) {
      baseColumns.push({
        field: 'action',
        headerText: 'Actions',
        template: ActionStatus,
        width: '14%',
        textAlign: 'Center' as TextAlign,
      })
    }
    return baseColumns
  }, [
    aiActorId,
    callHooks.showCallerDetails,
    callHooks.menuAnchor,
    callHooks.callLogs,
  ])

  const callsGridData = useMemo(
    () =>
      callHooks.filteredCallLogs.map((c) => ({
        ...c,
        _contact: formatPhoneNumber(
          ['Outgoing', 'Decline'].includes(c.call_type)
            ? c.receiver_number
            : c.caller_number,
        ),
        _call_date: formatCallDate(c.start_date_time, clinicTimeZone),
        _call_timestamp: getCallTimestamp(c.start_date_time),
      })),
    [callHooks.filteredCallLogs, clinicTimeZone, clinicDay],
  )

  const didSyncCallsGridRef = useRef(false)
  const prevCallsGridLengthRef = useRef(callsGridData.length)

  useEffect(() => {
    if (showEmptyState) {
      didSyncCallsGridRef.current = false
      prevCallsGridLengthRef.current = callsGridData.length
      return
    }

    const grid = callHooks.gridRef.current
    const previousLength = prevCallsGridLengthRef.current
    prevCallsGridLengthRef.current = callsGridData.length
    if (!grid) return
    if (!didSyncCallsGridRef.current) {
      didSyncCallsGridRef.current = true
      return
    }

    const page = grid.pageSettings?.currentPage ?? 1
    grid.dataSource = callsGridData
    grid.refresh()
    if (previousLength === callsGridData.length && page > 1) {
      grid.goToPage(page)
    }
  }, [callHooks.gridRef, callsGridData, showEmptyState])

  return (
    <div
      className={`calls-container${showEmptyState ? ' calls-container--empty' : ''}`}
    >
      <div className="mc-calls-main">
        {showEmptyState ? (
          <EmptyState
            className="mc-calls-empty"
            iconClass="mgc mgc_phone_fill"
            title="No call logs found"
            description={
              hasNoCallLogs
                ? "You don't have any call logs on record yet."
                : 'No call logs match your search.'
            }
          />
        ) : (
          <>
            <div className="cc-metrics-cards">
              {Object.entries(callHooks.callMetrics).map(([key, metric]) => (
                <div
                  key={key}
                  className={`cc-metrics-card ${getPercentageBadgeClassname(key, metric.classname ?? '')}`}
                >
                  <span className="cc-mc-title">
                    {metric.title}{' '}
                    <span
                      className="mgc_information_fill info-icon"
                      data-bs-toggle="tooltip"
                      title={metric.description}
                    />
                  </span>
                  <div className="cc-mc-value">
                    <span>
                      {key.toLowerCase().includes('time')
                        ? formatSecondsToString(metric.value)
                        : metric.value}
                    </span>
                    <span className="cc-mc-percentage">
                      {metric.classname
                        ? getPercentageIcon(metric.classname)
                        : ''}
                      {metric.percentage}%
                    </span>
                  </div>
                  <div className="cc-mc-date">
                    {key === 'csat' ? (
                      <div className="cc-mc-sentiment-bar">
                        <div
                          className="cc-mc-sentiment-segment negative"
                          style={{ flex: metric.negative ?? 0 }}
                        />
                        <div
                          className="cc-mc-sentiment-segment neutral"
                          style={{ flex: metric.neutral ?? 0 }}
                        />
                        <div
                          className="cc-mc-sentiment-segment positive"
                          style={{ flex: metric.value || 0 }}
                        />
                      </div>
                    ) : (
                      <span className="cc-mc-date-number">
                        As of{' '}
                        {new Date().toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div
              ref={callsGridSectionRef}
              className="message-grid-scroll-section"
              style={callsGridScrollStyle}
            >
              <DataGridComponent
                ref={callHooks.gridRef}
                allowPaging={true}
                pageSize={callHooks.callsGridPageSize}
                data={callsGridData}
                columns={columns}
                pageChange={callHooks.handlePageChange}
                customCSS="cc-call-logs"
                rowHeight={36}
                allowSelection={false}
                detailTemplate={callDetailTemplate}
                recordClick={onRowClick}
                dataBound={handleCallsGridDataBound}
                onActionComplete={handleCallsGridActionComplete}
              />
            </div>
            {callHooks.menuAnchor && (
              <FloatingMenu
                anchorRect={callHooks.menuAnchor.rect}
                menuItems={callHooks.menuAnchor.menuItems}
                selectedCall={callHooks.menuAnchor.call}
                onSelect={(args) => {
                  callHooks.handleSelect(args, callHooks.menuAnchor!.call)
                }}
                onClose={() => callHooks.setMenuAnchor(null)}
              />
            )}
          </>
        )}
      </div>
      {callHooks.showCallerDetails && callHooks.selectedCaller && (
        <CallerDetails
          call={callHooks.selectedCaller}
          onHide={() => callHooks.setShowCallerDetails(false)}
        />
      )}
      <MessageCenterDeleteCallLogModal
        show={callHooks.showDeleteModal}
        contactName={
          callHooks.selectedCaller?.contact
            ? getFullName(callHooks.selectedCaller.contact)
            : callHooks.selectedCaller?.caller_number
              ? formatPhoneNumber(callHooks.selectedCaller.caller_number)
              : 'Unknown caller'
        }
        onCancel={callHooks.onDeleteModalHide}
        onDelete={callHooks.onConfirmDeleteCall}
      />
      {callHooks.showAddContactModal && callHooks.selectedCaller && (
        <AddContactModal
          show={callHooks.showAddContactModal}
          fetchCalls={true}
          onHide={callHooks.handleCloseAddContactModal}
          initialContactData={{
            clinic_id: '',
            title: 'Mr.',
            first_name: '',
            last_name: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            cellphone:
              getCallDirection(callHooks.selectedCaller) === 'Incoming'
                ? callHooks.selectedCaller.caller_number
                : callHooks.selectedCaller.receiver_number,
            email: '',
          }}
        />
      )}
      {callHooks.showAISummaryModal && callHooks.selectedCaller && (
        <AISummaryModal
          callDetails={callHooks.selectedCaller}
          show={callHooks.showAISummaryModal}
          onHide={() => callHooks.setShowAISummaryModal(false)}
        />
      )}
      {callHooks.showAudioTranscriptModal &&
        callHooks.selectedCaller &&
        callHooks.selectedCaller.recording_url && (
          <AudioTranscription
            audioUrl={callHooks.selectedCaller.recording_url}
            show={callHooks.showAudioTranscriptModal}
            transcript={parseCallTranscript(
              callHooks.selectedCaller.transcript_text,
            )}
            toggleModal={callHooks.setShowAudioTranscriptModal}
            aiSummary={callHooks.selectedCaller.summary}
          />
        )}
    </div>
  )
}

export default MessageCenterCalls
